// FILE: SearchOverlay.jsx
import React, { useState, useEffect } from "react";
import { Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

const SearchOverlay = ({ isOpen, onClose, courses }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    setResults(
      query.trim() === ""
        ? []
        : courses.filter((c) =>
            c.title.toLowerCase().includes(query.toLowerCase())
          )
    );
  }, [query, courses]);

  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    if (isOpen) {
      document.addEventListener("keydown", esc);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", esc);
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-xl overflow-hidden animate-pop">
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            <Search className="w-5 h-5 text-[#0b9949]" />
            <span className="font-semibold">Search Courses</span>
          </div>
          <button
            onClick={onClose}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-200 hover:bg-gray-300 rounded-lg"
          >
            ESC <X className="w-4 h-4 text-red-500" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-5 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for courses..."
              className="w-full pl-10 pr-4 py-3 border-2 rounded-xl text-lg focus:border-[#0b9949] outline-none"
            />
          </div>
        </div>

        {/* Results */}
        <div className="max-h-[350px] overflow-y-auto p-4">
          {query.trim() === "" ? (
            <div className="flex flex-col items-center text-center py-6">

              {/* ⭐ Smaller Decorative Circles ⭐ */}
              <div className="relative w-48 h-48 mx-auto mb-6">
                {/* Outer dashed circle */}
                <div className="absolute inset-0 border border-dashed border-gray-200 rounded-full"></div>

                {/* Middle dashed */}
                <div className="absolute inset-[22%] border border-dashed border-gray-200 rounded-full"></div>

                {/* Inner dashed */}
                <div className="absolute inset-[40%] border border-dashed border-gray-200 rounded-full"></div>

                {/* Center icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center border-2 border-gray-200">
                    <Search className="w-6 h-6 text-[#0b9949]" />
                  </div>
                </div>
              </div>

              <p className="text-xl font-semibold text-gray-700">
                Start typing to search
              </p>
              <p className="text-gray-500 text-sm mt-1">
                Find the perfect course instantly.
              </p>
            </div>
          ) : results.length > 0 ? (
            <>
              <p className="text-sm text-gray-500 mb-3">
                Found {results.length} result{results.length !== 1 ? "s" : ""}
              </p>
              {results.map((course) => (
                <button
                  key={course.id}
                  onClick={() => {
                    navigate(`/course-details/${course.id}`);
                    onClose();
                  }}
                  className="w-full flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 border hover:border-[#0b9949] transition"
                >
                  <img
                    src={course.bannerImage}
                    className="w-14 h-14 rounded-lg object-cover"
                  />
                  <div className="text-left">
                    <p className="font-semibold">{course.title}</p>
                    <p className="text-sm text-gray-500">View details</p>
                  </div>
                </button>
              ))}
            </>
          ) : (
            <div className="text-center py-10">
              <Search className="w-10 h-10 mx-auto mb-3 text-gray-400" />
              <p className="text-xl font-semibold">No results found</p>

              <button
                onClick={() => setQuery("")}
                className="mt-4 bg-gray-100 hover:bg-gray-200 px-5 py-2 rounded-lg border"
              >
                Clear search
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 text-center text-sm text-gray-600">
          Need help?{" "}
          <a href="/contact" className="text-[#0b9949] underline">
            Contact us
          </a>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        .animate-fade { animation: fade .2s ease-out; }
        .animate-pop { animation: pop .25s ease-out; }

        @keyframes fade { from{opacity:0;} to{opacity:1;} }
        @keyframes pop { from{transform:scale(.95);opacity:0;} to{transform:scale(1);opacity:1;} }
      `}</style>
    </div>
  );
};

export default SearchOverlay;
